import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CITY_STORAGE_ENABLED } from '../config/features';

interface CityContextType {
  selectedCity: string;
  setSelectedCity: (city: string) => void;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

export const useCity = (): CityContextType => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
};

interface CityProviderProps {
  children: ReactNode;
}

export const CityProvider: React.FC<CityProviderProps> = ({ children }) => {
  // Загружаем город из localStorage или используем Омск по умолчанию
  const getInitialCity = (): string => {
    if (!CITY_STORAGE_ENABLED) {
      try {
        localStorage.removeItem('proteko-selected-city');
      } catch (_) {}
      return 'Омск';
    }

    try {
      const savedCity = localStorage.getItem('proteko-selected-city');
      if (savedCity) {
        return savedCity;
      }
    } catch (error) {
      console.error('Error loading city from localStorage:', error);
    }
    return 'Омск';
  };

  const [selectedCity, setSelectedCityState] = useState(getInitialCity);

  // Сохраняем город в localStorage при изменении
  useEffect(() => {
    if (!CITY_STORAGE_ENABLED) {
      try {
        localStorage.removeItem('proteko-selected-city');
      } catch (_) {}
      return;
    }

    try {
      localStorage.setItem('proteko-selected-city', selectedCity);
    } catch (error) {
      console.error('Error saving city to localStorage:', error);
    }
  }, [selectedCity]);

  const setSelectedCity = (city: string) => {
    setSelectedCityState(city);
  };

  const value: CityContextType = {
    selectedCity,
    setSelectedCity,
  };

  return (
    <CityContext.Provider value={value}>
      {children}
    </CityContext.Provider>
  );
};
